import { isNumber, isNumeric, isObject } from '@cutwise/utils';

import { ColorGrade } from '@/customGrading/constants/ColorGrade';
import { ColorGradeRange, ColorGradesFullType, ColorGradeValueType } from '@/customGrading/types/ColorGradesType';

const COLOR_GRADE_GROUPS: ColorGrade[][] = [
  [ColorGrade.D],
  [ColorGrade.E],
  [ColorGrade.F],
  [ColorGrade.G],
  [ColorGrade.H],
  [ColorGrade.I],
  [ColorGrade.J],
  [ColorGrade.K],
  [ColorGrade.L],
  [ColorGrade.M],
  [ColorGrade.N],
  [ColorGrade.O, ColorGrade.P],
  [ColorGrade.Q, ColorGrade.R],
  [ColorGrade.S, ColorGrade.T],
  [ColorGrade.U, ColorGrade.V],
  [ColorGrade.W, ColorGrade.X],
  [ColorGrade.Y, ColorGrade.Z],
];

class ColorGradeRepository {
  colorGradeFull?: ColorGradesFullType;

  private colorGrades?: Record<string, ColorGradeRange>;

  initColorGrades(colorGradeFull: ColorGradesFullType) {
    this.colorGradeFull = colorGradeFull;
    this.colorGrades = this.mapFullGradesToGroups(colorGradeFull);
  }

  private mapFullGradesToGroups(colorGradeFull: ColorGradesFullType) {
    return COLOR_GRADE_GROUPS.reduce<Record<string, ColorGradeRange>>((result, group) => {
      const min = colorGradeFull[group[0]].min;
      const max = colorGradeFull[group[group.length - 1]].max;

      result[group.join('-')] = { min, center: (min + max) / 2, max };

      return result;
    }, {});
  }

  get options() {
    return (isObject(this.colorGrades) ? Object.keys(this.colorGrades) : []) as string[];
  }

  get fullOptions() {
    return Object.keys(this.colorGradeFull ?? {}) as ColorGrade[];
  }

  get minMax() {
    if (!isObject(this.colorGrades)) {
      return [0, 0];
    }

    const sortedOptions = this.options.slice().sort((a, b) => {
      return this.colorGrades![a].min - this.colorGrades![b].min;
    });

    return [this.colorGrades[sortedOptions[0]].min, this.colorGrades[sortedOptions[sortedOptions.length - 1]].max];
  }

  getGradeByValue(value?: Nullable<number>) {
    if (!this.colorGrades) {
      return null;
    }

    if (!isNumber(value)) {
      return null;
    }

    const options = this.options;
    const floorValue = Math.floor(value);

    // for last grade we make strict comparison with max value,
    // for all others compare great or equal to min and less then max,
    // because max value equal to min value of next grade
    if (this.colorGrades[options[options.length - 1]].max === floorValue) {
      return options[options.length - 1];
    }

    return this.options.find((option) => {
      const { min, max } = this.colorGrades![option];

      if (isNumber(min) && isNumber(max)) {
        return floorValue >= min && floorValue < max;
      }

      return false;
    });
  }

  getValueByKey(type: ColorGradeValueType, key: string) {
    return this.colorGrades?.[key]?.[type];
  }

  isLastColorGrade(grade: string) {
    return this.options[this.options.length - 1] === grade;
  }

  isValidGradeValue(value?: Nullable<StringOrNumber>) {
    if (value === null) {
      return true;
    }

    if (!isNumeric(value)) {
      return false;
    }

    const [min, max] = this.minMax;
    const numericValue = +value;

    return numericValue >= min && numericValue <= max;
  }
}

export const colorGradeRepository = new ColorGradeRepository();
